import { z } from 'zod'
import { StaffRole } from './enums'

/**
 * Journey building blocks (design 14) beyond the basic definition in config.ts: date-based triggers,
 * consolidation per customer, computed values, internal recipients, call steps, transactional journeys,
 * the public intake API, and the views the console's journey editor and run list read.
 *
 * Every new field is optional with no default, so a journey stored before these existed compares equal
 * to itself in a change set.
 */
const HHMM = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use 24-hour HH:MM')
const KEY = z.string().regex(/^[a-z][a-z0-9_]{0,62}$/, 'lower-case letters, digits and underscores')

// ---------------------------------------------------------------------------- triggers
/**
 * What starts a run.
 * - `event` (the default): a domain event, e.g. `record.created`, `record.updated`, `record.saved` (either),
 *   `payment.failed`, optionally only when a field of the record equals a value (or is one of several).
 * - `date`: a date field on a record, with an offset ("3 days before the membership expires", "the day before the
 *   booking", "30 days before renewal"). A sweep starts the run when the moment arrives; moving the date starts
 *   a new run for the new date.
 */
export const JourneyTrigger = z.object({
  kind: z.enum(['event', 'date']).optional(),
  event: z.string().optional(),
  object: z.string().optional(),
  when: z.object({ field: z.string(), equals: z.unknown().optional(), in: z.array(z.unknown()).min(1).optional() }).optional(),
  /** Date triggers: the record's date or date-time field. */
  field: z.string().optional(),
  /** Date triggers: days relative to the date (negative = before), plus hours. */
  offset_days: z.number().int().min(-400).max(400).optional(),
  offset_hours: z.number().int().min(-23).max(23).optional(),
  /** Date triggers on a date-only field: the local time (company zone) the moment falls at. Defaults to 09:00. */
  at: HHMM.optional(),
  /** Date triggers: how late a missed moment may still start a run (a sweep that was down). Defaults to 24 hours. */
  catch_up_hours: z.number().positive().max(24 * 14).optional(),
  /**
   * `person`: one run per customer. A later matching record (a second failed payment) joins the customer's open run
   * instead of starting a second chase: one outcome per customer, later records appended.
   */
  per: z.enum(['record', 'person']).optional(),
  /** A record field whose value may start this journey only once, ever (e.g. one outcome per submission code). */
  once_per: z.string().optional(),
  /**
   * A run stopped because its record stopped matching (a status changed) is reopened at the step it was on,
   * not restarted from the first, when the record matches again within this many hours (a status
   * toggled away and back by mistake must not restart or end a chase). Defaults to 72; 0 starts a new run instead.
   */
  reopen_within_hours: z.number().int().min(0).max(24 * 30).optional(),
  /** Links a record that has no person to one, by a key both hold (e.g. a customer number). */
  person_match: z.object({ record_field: z.string(), person_field: z.string() }).optional(),
}).superRefine((t, ctx) => {
  if ((t.kind ?? 'event') === 'event') {
    if (!t.event) ctx.addIssue({ code: 'custom', message: 'An event trigger needs an event', path: ['event'] })
  } else {
    if (!t.object) ctx.addIssue({ code: 'custom', message: 'A date trigger needs the object whose date it watches', path: ['object'] })
    if (!t.field) ctx.addIssue({ code: 'custom', message: 'A date trigger needs the date field', path: ['field'] })
  }
  if (t.when && t.when.equals === undefined && !t.when.in) ctx.addIssue({ code: 'custom', message: 'Say what the field must equal', path: ['when'] })
})
export type JourneyTrigger = z.infer<typeof JourneyTrigger>

// ---------------------------------------------------------------------------- steps
export const JourneyStep = z.object({
  wait_hours: z.number().nonnegative(),
  /** Fallback order: the first channel the customer can be reached on (with consent) is used. `voice` is a call. */
  channels: z.array(z.enum(['sms', 'email', 'voice', 'whatsapp'])).min(1),
  template_key: z.string(),
  /**
   * WhatsApp outside the 24-hour customer service window: the approved Meta template this step sends, with each
   * of its {{1}}, {{2}}... filled from a journey value (`params[0]` fills {{1}}). Inside the window the step's own
   * wording goes as free text. Without a usable template the step falls back to its next channel, or holds.
   */
  whatsapp_template: z.object({ name: z.string().min(1).max(512), language: z.string().min(2).max(15), params: z.array(z.string().min(1)).max(20).default([]) }).optional(),
  /** Wording used instead once the customer has replied during the run (a chase after a reply). */
  reply_template_key: z.string().optional(),
  /** Who the step is for: the customer (default) or the company's own people (an internal notification). */
  to: z.enum(['customer', 'internal']).optional(),
  /**
   * Internal steps: fixed addresses and/or every active member of these roles. `reply_to: 'customer'` sets the
   * email's Reply-To to the customer's address, so the team can answer an enquiry straight from the inbox.
   * Internal steps go out whatever the customer's own flags, consent or reachability (the team must always hear of a
   * claim from a vulnerable or opted-out customer); customer steps of such a run are skipped.
   */
  internal: z.object({ emails: z.array(z.email()).optional(), roles: z.array(StaffRole).optional(), reply_to: z.enum(['customer']).optional() }).optional(),
  /** Call steps: what the AI call is for, in a sentence (the call's brief). */
  call: z.object({ purpose: z.string().min(3).max(500) }).optional(),
  /** Different wording when a computed value is zero (e.g. "cancellation fees" instead of "$0.00"). */
  variants: z.array(z.object({ if_zero: z.string(), template_key: z.string() })).optional(),
  /**
   * Only send this step if every earlier step reached the customer ("they ignored us" is only true if the
   * messages arrived). Otherwise the run is held for a person.
   */
  hold_unless_delivered: z.boolean().optional(),
  /**
   * The step goes even when the journey's contact cap is reached (a final warning: a customer who
   * replied once and went quiet still gets it on time, not days later when the first touch leaves the window).
   */
  cap_exempt: z.boolean().optional(),
  /**
   * After the customer has replied, the AI writes this step over the whole conversation (nudges after the first
   * touch answer what the customer last said, with the silence made explicit), with the step's wording as the
   * guidance and every reply check applied. A draft that fails a check, hands over or proposes an action is not
   * used: the step's own wording goes instead. Before any reply the step's wording always goes.
   */
  ai_after_reply: z.boolean().optional(),
}).superRefine((s, ctx) => {
  if (s.to === 'internal') {
    if (s.channels.some((c) => c !== 'email')) ctx.addIssue({ code: 'custom', message: 'Internal notifications go by email', path: ['channels'] })
    if (!s.internal?.emails?.length && !s.internal?.roles?.length) ctx.addIssue({ code: 'custom', message: 'Say who receives the internal notification', path: ['internal'] })
  }
  if (s.channels.includes('voice') && s.to !== 'internal' && !s.call) ctx.addIssue({ code: 'custom', message: 'A call step needs its purpose (what the call is for)', path: ['call'] })
})
export type JourneyStep = z.infer<typeof JourneyStep>

// ---------------------------------------------------------------------------- computed values
/**
 * Values computed in code at send time (design 14: amounts are never written by hand or by the AI).
 * - `multiply`: a number field times an amount in minor units (a fee per item × the number of items).
 * - `count_related`: the customer's records of another object linked by a key (active items under a contract),
 *   times an amount when given (a fee per active item).
 * - `date_offset`: a date field moved by some days (a quote valid for 30 days from when it was made).
 */
export const JourneyValue = z.discriminatedUnion('kind', [
  z.object({ key: KEY, kind: z.literal('multiply'), field: z.string(), by_minor: z.number().int() }),
  z.object({
    key: KEY, kind: z.literal('count_related'), object: z.string(), match_field: z.string(), record_field: z.string(),
    /**
     * Which related records count: those whose field is one of `in`, and/or not one of `not_in` (a record with no
     * value is not in any list, so `not_in: ['removed']` counts items whose status was never set).
     */
    where: z.object({ field: z.string(), in: z.array(z.unknown()).min(1).optional(), not_in: z.array(z.unknown()).min(1).optional() })
      .refine((w) => Boolean(w.in || w.not_in), { message: 'Say which values count (in) or which do not (not_in)' }).optional(),
    times_minor: z.number().int().optional(),
  }),
  z.object({ key: KEY, kind: z.literal('date_offset'), field: z.string(), days: z.number().int() }),
])
export type JourneyValue = z.infer<typeof JourneyValue>

/** The journey-wide options stored together (journeys.options). */
export const JourneyOptions = z.object({
  /** `transactional`: confirmations and notifications. No contact cap, no waiting for delivery between steps. */
  kind: z.enum(['journey', 'transactional']).optional(),
  values: z.array(JourneyValue).optional(),
  /** How long after the last step the run waits for an outcome before it completes (default 24 hours). */
  final_wait_hours: z.number().nonnegative().max(24 * 30).optional(),
  /** Append "Reply STOP to opt out." to SMS. Defaults to on for marketing and off otherwise. */
  sms_opt_out: z.boolean().optional(),
  /** Wait for delivery truth before the next step. Defaults to on, and off for transactional journeys. */
  wait_for_delivery: z.boolean().optional(),
  /**
   * SMS routes that rarely send a final delivery receipt (a `sent` text with no receipt need not block the
   * cadence): a text accepted by the provider and not reported failed within this many hours counts as delivered.
   * A failure receipt that arrives later still marks it failed. Unset: only a delivery receipt counts.
   */
  sms_delivery_inferred_hours: z.number().positive().max(72).optional(),
  /**
   * The run stops when the customer has a related record that shows the journey's job is done another way
   * (a quote follow-up: a sign-up carrying the quote number, or any sign-up by the same customer since the
   * run started).
   */
  stop_if_related: z.object({
    object: KEY,
    /** The related record's field holding the run record's key (e.g. a sign-up's quote_number). */
    match_field: z.string().optional(),
    /** The run record's field it must equal (e.g. the quote's number). Without both, any such record created since the run started counts. */
    record_field: z.string().optional(),
  }).optional(),
  /**
   * When the AI may mark the run's conversation resolved, which ends the run for good (e.g.:
   * assent, already paid or a final escalation count; a conditional "maybe", a scam worry or a wrong number do not).
   * Stated to the AI on every reply in the conversation while the run is going.
   */
  resolution_rule: z.string().min(10).max(600).optional(),
})
export type JourneyOptions = z.infer<typeof JourneyOptions>
export const JOURNEY_OPTION_KEYS = ['kind', 'values', 'final_wait_hours', 'sms_opt_out', 'wait_for_delivery', 'sms_delivery_inferred_hours', 'stop_if_related', 'resolution_rule'] as const
/** The options of a journey definition, as stored in journeys.options (only those that are set). */
export function journeyOptions(v: Record<string, unknown>): JourneyOptions {
  return Object.fromEntries(JOURNEY_OPTION_KEYS.filter((k) => v[k] !== undefined && v[k] !== null).map((k) => [k, v[k]])) as JourneyOptions
}

// ---------------------------------------------------------------------------- outbound calls
/** What a call step asks the voice service for (built by the voice workstream). */
export interface OutboundCallRequest { company_id: string; person_id: string; conversation_id: string | null; purpose: string; journey_run_id?: string }
export type OutboundCallResult = { ok: true; call_id: string } | { ok: false; reason: string }
/**
 * The voice service's entry point for an AI call to a customer. Journeys call it for a `voice` step when it is
 * registered (services/journeyCalls.ts); until then a call step is held for a person to make the call.
 */
export type StartOutboundCall = (deps: unknown, companyId: string, personId: string, conversationId: string | null, purpose: string, opts?: { journeyRunId?: string | null }) => Promise<OutboundCallResult>

// ---------------------------------------------------------------------------- console views
export interface JourneyRunView {
  id: string; journey_key: string; journey_name: string; person_id: string; person_name: string; record_id: string | null
  record_ids: string[]; conversation_id: string | null; conversation_ref: string | null
  status: 'active' | 'waiting_delivery' | 'paused' | 'completed' | 'stopped'; step: number; steps: number
  pause_reason: string | null; wait_reason: string | null; stop_reason: string | null
  /** In a sentence: why the run is where it is (waiting, paused or stopped), for the run list. */
  why: string
  next_at: string | null; last_step_at: string | null; paused_at: string | null; ended_at: string | null
  created_at: string; updated_at: string; generation: number; trigger_key: string | null
  can: { pause: boolean; resume: boolean; restart: boolean; stop: boolean }
  /** The run tells only the team (the customer is vulnerable, a wrong number, opted out or unreachable): why. */
  internal_only: string | null
}
export interface JourneyRunMessage {
  id: string; channel: string; status: string; body_text: string; subject: string | null; error: string | null; step: number | null; template: string | null
  internal: boolean; created_at: string; sent_at: string | null; delivered_at: string | null; send_after: string | null
}
export interface JourneyRunDetail extends JourneyRunView { messages: JourneyRunMessage[]; events: { at: string; action: string; by: string | null; detail: Record<string, unknown> }[] }

/** A variable a template may use, for the editor's picker. */
export interface TemplateVariable { key: string; label: string; source: 'person' | 'company' | 'record' | 'computed' | 'run'; example: string | null }

export interface JourneyCheck { ok: boolean; level: 'error' | 'warning'; step: number | null; message: string }

/** One customer in a dry run: who would be contacted, on what, with what words. Nothing is sent or written. */
export interface JourneyDryRunRow {
  person_id: string; person_name: string; record_id: string | null; record_title: string | null
  /** When the run would start (date triggers) or "now". */
  starts_at: string | null
  outcome: 'would_send' | 'would_hold' | 'would_skip'
  reason: string
  channel: string | null; to: string | null; subject: string | null; text: string | null
  deferred_until: string | null
}
export interface JourneyDryRun { journey_key: string; evaluated: number; rows: JourneyDryRunRow[]; checks: JourneyCheck[]; summary: Record<'would_send' | 'would_hold' | 'would_skip', number> }

export interface TemplatePreview {
  subject: string | null; text: string; html: string | null; missing: string[]; values: Record<string, string>
  /**
   * The words exactly as a text we start goes out: the company's opt-out wording and sender name are added at send
   * time for some purposes, so regulated wording shows here as the customer will read it. Null when not a text.
   */
  sms_as_sent?: { text: string; added: string | null; segments: number; purpose: string } | null
}

// ---------------------------------------------------------------------------- public intake (website forms)
/**
 * The keyed public intake API (a company's website forms: quotes, sign-ups, claims, enquiries). Card and bank
 * numbers are never accepted (bank payments are set up by a person or the payment rail, never stored here).
 */
export const IntakeSubmission = z.object({
  person: z.object({
    name: z.string().trim().min(1).max(200).optional(),
    email: z.string().trim().max(254).optional(),
    phone: z.string().trim().max(40).optional(),
  }),
  /** Field values for the form's object (e.g. a quote's product and quantity). */
  data: z.record(z.string(), z.unknown()).default({}),
  /**
   * What the customer chose, item by item, for a form priced on the server (each item with
   * its product, brand and age). The price is always computed from the price list, never taken from here.
   */
  items: z.array(z.object({
    /** Product name or SKU from the price list (e.g. "Standard plan"). */
    product: z.string().trim().min(1).max(200),
    quantity: z.number().int().min(1).max(50).default(1),
    /** What the item is (e.g. "Laptop"). */
    description: z.string().trim().max(200).optional(),
    brand: z.string().trim().max(100).optional(),
    /** Age in whole years, checked against the product's eligibility. */
    age_years: z.number().min(0).max(200).optional(),
  })).max(50).optional(),
  /** What the customer agreed to, with the wording version they saw (acceptance evidence). */
  consent: z.object({
    /** Marketing by any channel (one box on the form). */
    marketing: z.boolean().optional(),
    /** Separate boxes, for forms that ask them apart: marketing by email, and marketing by text. */
    marketing_email: z.boolean().optional(),
    marketing_sms: z.boolean().optional(),
    terms_version: z.string().max(100).optional(),
  }).optional(),
  /** Marketing attribution (UTM and referrer), stored with the submission. */
  attribution: z.record(z.string(), z.string().max(500)).optional(),
  /** The website chat session this came from (the widget's session token), so the conversation and the record are linked. */
  chat_session_token: z.string().max(200).optional(),
  /** Cloudflare Turnstile token from a browser form, when the key requires it. */
  turnstile_token: z.string().max(4096).optional(),
}).refine((s) => Boolean(s.person.email || s.person.phone), { message: 'An email address or a phone number is needed', path: ['person'] })
export type IntakeSubmission = z.infer<typeof IntakeSubmission>

export const IntakeForm = z.object({
  /** The form name in the URL: /public/v1/intake/<form>. */
  form: KEY,
  /** The object the submission becomes a record of. */
  object: z.string(),
  /** The record's reference field, filled with a new reference (e.g. Q-1042). */
  reference_field: z.string().optional(),
  reference_prefix: z.string().regex(/^[A-Z]{1,6}$/).optional(),
  /** Fields set by the server, never by the form (e.g. status: new). */
  fixed: z.record(z.string(), z.unknown()).optional(),
  /** Days a submission stays valid, written to `valid_until_field` (a quote held for 30 days). */
  valid_days: z.number().int().positive().max(365).optional(),
  valid_until_field: z.string().optional(),
  /**
   * Priced on the server from the price list (a price is never trusted from the browser).
   * The submission names its products in `items` (or one product in `product_field` and a count in
   * `quantity_field`); the total goes in `total_field`, the count in `quantity_field`, a line per item
   * in `summary_field`, and per product tag in `tag_counts` how many items carry it (a boolean field: whether any do). A total sent by the website is replaced, and a different
   * one raises an alarm (the website's prices are out of date).
   */
  pricing: z.object({
    total_field: z.string(),
    product_field: z.string().optional(),
    quantity_field: z.string().optional(),
    summary_field: z.string().optional(),
    tag_counts: z.array(z.object({ tag: z.string().trim().toLowerCase().min(1).max(40), field: z.string() })).max(10).optional(),
  }).optional(),
})
export type IntakeForm = z.infer<typeof IntakeForm>

export const IntakeKeyInput = z.object({
  name: z.string().trim().min(1).max(100),
  /** `browser`: a publishable key used from web pages, limited to the listed origins. `server`: each request is signed with the secret. */
  kind: z.enum(['browser', 'server']),
  forms: z.array(IntakeForm).min(1),
  allowed_origins: z.array(z.url()).default([]),
  require_turnstile: z.boolean().default(false),
  /** Requests a minute per key and client address. */
  rate_per_minute: z.number().int().positive().max(600).default(20),
})
export type IntakeKeyInput = z.infer<typeof IntakeKeyInput>

export interface IntakeKeyView {
  id: string; name: string; kind: 'browser' | 'server'; key_prefix: string; forms: IntakeForm[]; allowed_origins: string[]; require_turnstile: boolean
  rate_per_minute: number; active: boolean; created_at: string; last_used_at: string | null; submissions_24h: number
}
/** Returned once, when a key is made: the full key (and the signing secret for server keys). */
export interface IntakeKeyCreated extends IntakeKeyView { key: string; secret: string | null }

export interface IntakeSubmissionView {
  id: string; form: string; status: 'accepted' | 'rejected'; reference: string | null; record_id: string | null; person_id: string | null
  error: string | null; origin: string | null; created_at: string
  /** Marketing attribution (UTM, referrer) and what the customer agreed to, as the website sent them. */
  attribution: Record<string, string> | null
  consent: Record<string, unknown> | null
  /** The website chat the visitor came from, when the form carried its session. */
  conversation_id: string | null
}
